import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  useParams,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import Spinner from "../Spinner";
import { API_URL, MEMBERS_API_URL } from "../../config/api.config";
import "./ProgressNotes.css";
import FirstReportPanel from "../WeekOneReport/FirstReportPanel";
import { api as firstReportApi } from "../WeekOneReport/api";

// Report types, in the order a member goes through them. The page pre-selects one from the member's history.
const REPORT_TYPES = [
  { id: "first", label: "First report" },
  { id: "transitional", label: "Transitional (not built yet)", disabled: true },
  { id: "progress", label: "Progress report" },
];

// ----------------------
// Dummy User Data
// ----------------------
const dummyUsers = [
  { id: 1, firstName: "John", lastName: "Doe", lastReport: "2023-01-01" },
  { id: 2, firstName: "Jane", lastName: "Smith", lastReport: "2023-01-02" },
  { id: 3, firstName: "Alice", lastName: "Johnson", lastReport: "2023-01-03" },
];

// ----------------------
// User Report List Screen
// ----------------------
const UserReportList = () => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetch(MEMBERS_API_URL, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((data) => {
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
            ? data.data
            : Array.isArray(data?.members)
              ? data.members
              : [];
        setUsers(list);
      })
      .catch((err) => {
        console.error("Error fetching members:", err);
        setError(err.message || "Failed to load members");
        if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
          setUsers(dummyUsers);
          setError(null);
        } else {
          setUsers([]);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  // Filter users based on search term
  const filteredUsers = users.filter((user) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      user.firstName?.toLowerCase().includes(searchLower) ||
      user.lastName?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="progress-notes-page">
      <Link to="/" className="progress-notes-back">
        ← Back
      </Link>
      <h1 className="progress-notes-title">Progress Notes</h1>

      <div className="progress-notes-search-wrap">
        <input
          type="text"
          placeholder="Search by first name or last name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="progress-notes-search"
        />
        <span className="progress-notes-meta">
          {filteredUsers.length} of {users.length} members
        </span>
      </div>

      {loading ? (
        <p className="progress-notes-loading">Loading users...</p>
      ) : error ? (
        <p className="progress-notes-empty">
          {error}. Check the console and ensure the members API is reachable (e.g. CORS if running locally).
        </p>
      ) : filteredUsers.length === 0 ? (
        <p className="progress-notes-empty">
          {users.length === 0
            ? "No members found."
            : `No members found matching "${searchTerm}"`}
        </p>
      ) : (
        <div className="progress-notes-cards">
          {filteredUsers.map((user) => {
            const userId = user.id ?? user._id;
            return (
              <div key={userId} className="progress-notes-card">
                <h3 className="progress-notes-card-title">
                  {user.firstName} {user.lastName}
                </h3>
                <p className="progress-notes-card-sub">
                  Last report: {user.lastReport || "—"}
                </p>
                <Link
                  to={`/user/${userId}/reports`}
                  className="progress-notes-btn-open"
                >
                  Open
                </Link>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ----------------------
// Dummy Report Data Template
// ----------------------
const defaultReportData = {
  activity: {
    highlights: ["Highlight 1", "Highlight 2", "Highlight 3"],
    recommendations: [
      "Recommendation 1",
      "Recommendation 2",
      "Recommendation 3",
    ],
    detailed_notes: ["Note 1", "Note 2", "Note 3", "Note 4", "Note 5"],
  },
  glucose: {
    highlights: ["Highlight 1", "Highlight 2", "Highlight 3"],
    recommendations: [
      "Recommendation 1",
      "Recommendation 2",
      "Recommendation 3",
    ],
    detailed_notes: ["Note 1", "Note 2", "Note 3", "Note 4", "Note 5"],
  },
  nutrition: {
    highlights: ["Highlight 1", "Highlight 2", "Highlight 3"],
    recommendations: [
      "Recommendation 1",
      "Recommendation 2",
      "Recommendation 3",
    ],
    detailed_notes: ["Note 1", "Note 2", "Note 3", "Note 4", "Note 5"],
  },
  sleep: {
    highlights: ["Highlight 1", "Highlight 2", "Highlight 3"],
    recommendations: [
      "Recommendation 1",
      "Recommendation 2",
      "Recommendation 3",
    ],
    detailed_notes: ["Note 1", "Note 2", "Note 3", "Note 4", "Note 5"],
  },
};

// Dummy list of available reports for selection
const dummyAvailableReports = [
  { id: "report1", label: "Report 1", data: defaultReportData },
  {
    id: "report2",
    label: "Report 2",
    data: {
      activity: {
        highlights: ["Act H 1", "Act H 2", "Act H 3"],
        recommendations: ["Act R 1", "Act R 2", "Act R 3"],
        detailed_notes: ["Act N 1", "Act N 2", "Act N 3", "Act N 4", "Act N 5"],
      },
      glucose: {
        highlights: ["Glu H 1", "Glu H 2", "Glu H 3"],
        recommendations: ["Glu R 1", "Glu R 2", "Glu R 3"],
        detailed_notes: ["Glu N 1", "Glu N 2", "Glu N 3", "Glu N 4", "Glu N 5"],
      },
      nutrition: {
        highlights: ["Nut H 1", "Nut H 2", "Nut H 3"],
        recommendations: ["Nut R 1", "Nut R 2", "Nut R 3"],
        detailed_notes: ["Nut N 1", "Nut N 2", "Nut N 3", "Nut N 4", "Nut N 5"],
      },
      sleep: {
        highlights: ["Sleep H 1", "Sleep H 2", "Sleep H 3"],
        recommendations: ["Sleep R 1", "Sleep R 2", "Sleep R 3"],
        detailed_notes: [
          "Sleep N 1",
          "Sleep N 2",
          "Sleep N 3",
          "Sleep N 4",
          "Sleep N 5",
        ],
      },
    },
  },
];

// ----------------------
// User Report Detail Screen
// ----------------------
const UserReportDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // ── state ────────────────────────────────────────────────────────────
  const [member, setMember] = useState(null);
  const [availableReports, setAvailableReports] = useState(null);
  const [progressLoaded, setProgressLoaded] = useState(false);

  // report type: from the URL when set (e.g. coming back from a first-report review), else pre-selected below
  const [firstReports, setFirstReports] = useState(null);
  const [firstReportsError, setFirstReportsError] = useState(null);
  const pickedType = searchParams.get("type");

  // currently-loaded report
  const [reportData, setReportData] = useState(null);
  const [reportId, setReportId] = useState("");
  const [reportFileId, setReportFileId] = useState("");

  // which option is selected in the <select>
  const [selectedReportId, setSelectedReportId] = useState("");
  const [selectedReportName, setSelectedReportName] = useState("");

  // Date range for report generation
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [useCustomDateRange, setUseCustomDateRange] = useState(false);

  // UX flags
  const [generateLoading, setGenerateLoading] = useState(false);
  const [savingLoading, setSavingLoading] = useState(false);
  const [reportGenerated, setReportGenerated] = useState(false);

  // ── fetch member name (for display) ───────────────────────────────────
  useEffect(() => {
    fetch(MEMBERS_API_URL, { method: "GET", headers: { "Content-Type": "application/json" } })
      .then((res) => res.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        const found = list.find(
          (m) => String(m?.id ?? m?._id ?? "") === String(userId)
        );
        setMember(found ? { firstName: found.firstName ?? "", lastName: found.lastName ?? "" } : null);
      })
      .catch(() => setMember(null));
  }, [userId]);

  // ── initial fetch of existing reports ────────────────────────────────
  useEffect(() => {
    fetch(
      `${API_URL}/${userId}`
    )
      .then((res) => res.json())
      .then((data) => setAvailableReports(data))
      .catch((err) => console.error("Error loading reports:", err))
      .finally(() => setProgressLoaded(true));
  }, [userId]);

  useEffect(() => {
    firstReportApi()
      .listReports(userId)
      .then(setFirstReports)
      .catch((err) => {
        setFirstReportsError(`Couldn't load first reports: ${err.message}`);
        setFirstReports([]);
      });
  }, [userId]);

  // First report until one has gone to the member; progress after that, or for anyone who already has progress reports.
  const firstSent = (firstReports || []).some((r) => r.status !== "draft" && r.approval?.purpose === "member");
  const hasProgress = Array.isArray(availableReports) && availableReports.length > 0;
  const suggestedType = firstReports === null || !progressLoaded ? null : firstSent || hasProgress ? "progress" : "first";
  const reportType = pickedType || suggestedType;
  const suggestedWhy = firstSent
    ? "a first report has gone to this member"
    : hasProgress
      ? "this member already has progress reports"
      : "no first report has gone to this member yet";

  // ── helpers ──────────────────────────────────────────────────────────
  const updateReportField = (section, field, idx, value) => {
    setReportData((prev) => {
      const updated = { ...prev };
      updated[section] = { ...updated[section] };
      updated[section][field] = [...updated[section][field]];
      updated[section][field][idx] = value;
      return updated;
    });
  };

  // ── Generate new report ──────────────────────────────────────────────
  const handleGenerateReport = () => {
    let finalStartDate, finalEndDate;

    if (useCustomDateRange) {
      // Validate custom date range
      if (!startDate || !endDate) {
        alert("Please select both start date and end date");
        return;
      }
      
      if (new Date(startDate) > new Date(endDate)) {
        alert("Start date cannot be after end date");
        return;
      }

      finalStartDate = startDate;
      finalEndDate = endDate;
    } else {
      // Use automatic 15-day range (last 15 days)
      const today = new Date();
      const fifteenDaysAgo = new Date();
      fifteenDaysAgo.setDate(today.getDate() - 15);
      
      // For 15 days (10th to 24th), end date should be yesterday (today - 1)
      const yesterday = new Date();
      yesterday.setDate(today.getDate() - 1);
      
      finalStartDate = fifteenDaysAgo.toISOString().split('T')[0];
      finalEndDate = yesterday.toISOString().split('T')[0];
    }

    // Debug: Log what dates are being sent
    console.log("=== REPORT GENERATION DEBUG ===");
    console.log("Use Custom Date Range:", useCustomDateRange);
    console.log("Final Start Date:", finalStartDate);
    console.log("Final End Date:", finalEndDate);
    console.log("Request Body:", { memberId: userId, startDate: finalStartDate, endDate: finalEndDate });
    console.log("================================");

    // hide whatever is on screen while we generate
    setGenerateLoading(true);
    setReportData(null);
    setSelectedReportId("");
    setSelectedReportName("");
    setReportGenerated(false);

    fetch(API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ memberId: userId, start_date: finalStartDate, end_date: finalEndDate }),
    })
      .then((res) => {
        console.log("Response status:", res.status);
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        console.log("Backend Response:", data);
        
        // Check if data.data exists
        if (!data.data) {
          console.error("Invalid response: data.data is undefined", data);
          alert(`Error: Invalid response from server for member ${userId}. Please check backend logs.`);
          return;
        }
        
        const newRpt = {
          reportId: data.data._id,
          reportName:
            data.data.reportName || `Report ${finalStartDate} to ${finalEndDate}`,
          data: data.data.recommendations_highlights,
        };

        // ① add it to the dropdown list
        setAvailableReports((prev) => (prev ? [...prev, newRpt] : [newRpt]));

        // ② select it
        setSelectedReportId(newRpt.reportId);
        setSelectedReportName(newRpt.reportName);

        // ③ show its details
        setReportData(newRpt.data);
        setReportId(newRpt.reportId);
        setReportFileId(data.driveFileId);
        setReportGenerated(true);

        alert("Report generated successfully!");
      })
      .catch((err) => {
        console.error("Error generating report:", err);
        alert("Error generating report");
      })
      .finally(() => setGenerateLoading(false));
  };

  // ── select an existing report ────────────────────────────────────────
  const handleSelectReport = (e) => {
    const rptId = e.target.value;
    const rpt = availableReports?.find((r) => r.reportId === rptId);
    if (!rpt) return;

    setSelectedReportId(rpt.reportId);
    setSelectedReportName(rpt.reportName);
    setReportData(rpt.data);
    setReportId(rpt.reportId);
    // Reset reportGenerated when selecting existing reports
    setReportGenerated(false);
  };

  // ── save changes ─────────────────────────────────────────────────────
  const handleSaveChanges = (e) => {
    e.preventDefault();
    setSavingLoading(true);

    fetch(API_URL, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        reportId,
        updates: { recommendations_highlights: reportData },
      }),
    })
      .then((res) => res.json())
      .then((json) => {
        if (!json.updated) throw new Error("Report not updated");
        alert("Report saved successfully!");
      })
      .catch((err) => alert("Report not saved. Error: " + err))
      .finally(() => setSavingLoading(false));
  };

  const memberDisplayName =
    member && (member.firstName || member.lastName)
      ? [member.firstName, member.lastName].filter(Boolean).join(" ")
      : null;

  // ── render ───────────────────────────────────────────────────────────
  return (
    <div className="progress-notes-page progress-notes-detail-page">
      <button
        type="button"
        className="progress-notes-back"
        onClick={() => navigate("/biweekly-reports")}
        style={{ background: "none", border: "none", cursor: "pointer" }}
      >
        ← Back
      </button>

      <h1 className="progress-notes-member-name">
        {memberDisplayName ?? `Member ${userId}`}
      </h1>
      <div className="progress-notes-toolbar">
        <label htmlFor="report-type" className="progress-notes-field-label" style={{ display: "block" }}>
          Report type
        </label>
        <select
          id="report-type"
          value={reportType || ""}
          onChange={(e) => setSearchParams({ type: e.target.value }, { replace: true })}
          disabled={!reportType}
          className="progress-notes-select"
        >
          {!reportType && <option value="">Loading…</option>}
          {REPORT_TYPES.map((t) => (
            <option key={t.id} value={t.id} disabled={t.disabled}>
              {t.label}
            </option>
          ))}
        </select>
        {suggestedType && (
          <span className="progress-notes-meta" style={{ marginLeft: 12 }}>
            Suggested: {REPORT_TYPES.find((t) => t.id === suggestedType).label.toLowerCase()}, since {suggestedWhy}
          </span>
        )}
      </div>

      {reportType === "first" && (
        <FirstReportPanel memberId={userId} reports={firstReports} loadError={firstReportsError} />
      )}

      {reportType === "progress" && (<>
      <p className="progress-notes-member-subtitle">
        Report Details
        {selectedReportName && <> — {selectedReportName}</>}
      </p>

      <div className="progress-notes-tabs">
        <span className="progress-notes-tab active">Report Details</span>
      </div>

      <div className="progress-notes-toolbar">
        <div className="progress-notes-form-group">
          <label style={{ marginRight: "15px" }}>
            <input
              type="radio"
              checked={!useCustomDateRange}
              onChange={() => setUseCustomDateRange(false)}
              style={{ marginRight: "6px" }}
            />
            Automatic (Last 15 Days)
          </label>
          <label>
            <input
              type="radio"
              checked={useCustomDateRange}
              onChange={() => setUseCustomDateRange(true)}
              style={{ marginRight: "6px" }}
            />
            Custom Date Range
          </label>
        </div>

        <div className="progress-notes-meta" style={{ marginBottom: "12px" }}>
          {!useCustomDateRange
            ? (() => {
                const today = new Date();
                const fifteenDaysAgo = new Date();
                fifteenDaysAgo.setDate(today.getDate() - 15);
                const yesterday = new Date();
                yesterday.setDate(today.getDate() - 1);
                return `Date Range: ${fifteenDaysAgo.toLocaleDateString()} to ${yesterday.toLocaleDateString()}`;
              })()
            : `Date Range: ${startDate ? new Date(startDate + "T00:00:00").toLocaleDateString() : "—"} to ${endDate ? new Date(endDate + "T00:00:00").toLocaleDateString() : "—"}`}
        </div>

        {useCustomDateRange && (
          <div style={{ marginBottom: "12px" }}>
            <label style={{ marginRight: "10px" }}>Start Date:</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="progress-notes-input"
              style={{ marginRight: "15px" }}
            />
            <label style={{ marginRight: "10px" }}>End Date:</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="progress-notes-input"
            />
          </div>
        )}

        <div>
          {generateLoading ? (
            <span className="progress-notes-meta">Generating Report...</span>
          ) : (
            <button
              type="button"
              onClick={handleGenerateReport}
              disabled={useCustomDateRange && (!startDate || !endDate)}
              className="progress-notes-btn-primary"
            >
              Generate Report
            </button>
          )}

          <select
            value={selectedReportId}
            onChange={handleSelectReport}
            disabled={!availableReports}
            className="progress-notes-select"
          >
            <option value="" disabled>
              {availableReports ? "Select a report" : "Loading..."}
            </option>
            {availableReports?.map((r) => (
              <option key={r.reportId} value={r.reportId}>
                {r.reportName}
              </option>
            ))}
          </select>

          {reportGenerated && reportFileId && (
            <button
              type="button"
              onClick={() =>
                window.open(
                  `https://drive.google.com/file/d/${reportFileId}/view`
                )
              }
              className="progress-notes-btn-secondary"
            >
              Report Link
            </button>
          )}
        </div>
      </div>

      {reportData && (
        <form onSubmit={handleSaveChanges}>
          {Object.keys(reportData).map((section) => (
            <div key={section} className="progress-notes-form-group">
              <h3 className="progress-notes-section-title">
                {section.charAt(0).toUpperCase() + section.slice(1)}
              </h3>
              {["highlights", "recommendations", "detailed_notes"].map(
                (field) => (
                  <div key={field} style={{ marginBottom: "12px" }}>
                    <div className="progress-notes-field-label">
                      {field.replace("_", " ")}
                    </div>
                    {reportData[section][field].map((item, idx) => (
                      <input
                        key={idx}
                        type="text"
                        value={item}
                        onChange={(e) =>
                          updateReportField(section, field, idx, e.target.value)
                        }
                        className="progress-notes-input"
                        style={{ display: "block", marginBottom: "6px" }}
                      />
                    ))}
                  </div>
                )
              )}
            </div>
          ))}
          {savingLoading ? (
            <p className="progress-notes-meta">Saving...</p>
          ) : (
            <button type="submit" className="progress-notes-btn-primary">
              Save Changes
            </button>
          )}
        </form>
      )}
      </>)}

      <div style={{ marginTop: "24px" }}>
        <button
          type="button"
          onClick={() => navigate("/biweekly-reports")}
          className="progress-notes-btn-secondary"
        >
          Back to Progress Notes
        </button>
      </div>
    </div>
  );
};

export { UserReportList, UserReportDetail };
